import * as THREE from 'three';

/** A transient visual effect; `update` returns false once finished. */
export interface Effect {
  readonly obj: THREE.Object3D;
  update(dt: number): boolean;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Crescent slash: a flat arc sweeping across a cone, bright at the outer edge.

const SLASH_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uProgress;
uniform float uFade;
varying vec2 vUv;
void main() {
  float u = vUv.x;           // along the arc (0..1 in sweep order)
  float v = vUv.y;           // inner (0) .. outer (1)
  float head = smoothstep(uProgress - 0.55, uProgress, u) * step(u, uProgress);
  float edge = pow(v, 2.2) * smoothstep(1.0, 0.86, v);
  float core = smoothstep(0.7, 0.95, v) * smoothstep(1.0, 0.93, v);
  float a = head * (edge * 0.9 + core) * (1.0 - uFade);
  vec3 col = mix(uColor, vec3(1.0), core * 0.6) * (1.5 + core * 2.5);
  gl_FragColor = vec4(col, a);
}`;

const SIMPLE_VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

function arcGeometry(radius: number, angle: number, inner: number, segs = 28): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= segs; i++) {
    const u = i / segs;
    const a = -angle / 2 + u * angle;
    for (let j = 0; j <= 1; j++) {
      const r = j === 0 ? inner : radius;
      pos.push(Math.sin(a) * r, 0, Math.cos(a) * r);
      uv.push(u, j);
    }
  }
  for (let i = 0; i < segs; i++) {
    const a = i * 2;
    idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}

export class SlashEffect implements Effect {
  readonly obj: THREE.Mesh;
  private t = 0;
  private mat: THREE.ShaderMaterial;
  constructor(
    origin: THREE.Vector3,
    facing: number,
    radius: number,
    angle: number,
    color: THREE.Color,
    private sweep = 0.14,
    private fade = 0.16,
    flip = false,
    tilt = 0,
  ) {
    this.mat = new THREE.ShaderMaterial({
      vertexShader: SIMPLE_VERT,
      fragmentShader: SLASH_FRAG,
      uniforms: { uColor: { value: color.clone() }, uProgress: { value: 0 }, uFade: { value: 0 } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const geo = arcGeometry(radius, Math.min(angle, Math.PI * 2 - 0.01), radius * 0.35);
    if (flip) {
      const uvs = geo.getAttribute('uv') as THREE.BufferAttribute;
      for (let i = 0; i < uvs.count; i++) uvs.setX(i, 1 - uvs.getX(i));
    }
    this.obj = new THREE.Mesh(geo, this.mat);
    this.obj.position.copy(origin);
    this.obj.rotation.set(tilt, facing, 0, 'YXZ');
    this.obj.renderOrder = 12;
  }
  update(dt: number): boolean {
    this.t += dt;
    const u = this.mat.uniforms as { uProgress: { value: number }; uFade: { value: number } };
    u.uProgress.value = Math.min(1.25, this.t / this.sweep);
    u.uFade.value = Math.max(0, (this.t - this.sweep) / this.fade);
    return this.t < this.sweep + this.fade;
  }
  dispose(): void {
    this.obj.geometry.dispose();
    this.mat.dispose();
  }
}

// ---------------------------------------------------------------------------
// Expanding ring shockwave (also used for donut eruptions).

const RING_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uAlpha;
uniform float uThick;
varying vec2 vUv;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float band = smoothstep(1.0 - uThick, 1.0 - uThick * 0.4, r) * smoothstep(1.0, 0.94, r);
  // Faint fill inside the ring only (the quad's corners reach r = 1.41).
  float inner = smoothstep(0.0, 1.0, r) * 0.12 * (1.0 - smoothstep(0.94, 1.0, r));
  gl_FragColor = vec4(uColor * (1.0 + band * 2.0), (band + inner) * uAlpha);
}`;

export class RingEffect implements Effect {
  readonly obj: THREE.Mesh;
  private t = 0;
  private mat: THREE.ShaderMaterial;
  constructor(
    origin: THREE.Vector3,
    private radius: number,
    color: THREE.Color,
    private dur = 0.35,
    thickness = 0.22,
    private startScale = 0.15,
  ) {
    this.mat = new THREE.ShaderMaterial({
      vertexShader: SIMPLE_VERT,
      fragmentShader: RING_FRAG,
      uniforms: { uColor: { value: color.clone() }, uAlpha: { value: 1 }, uThick: { value: thickness } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.obj = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.mat);
    this.obj.rotation.x = -Math.PI / 2;
    this.obj.position.copy(origin);
    this.obj.position.y += 0.06;
    this.obj.renderOrder = 11;
    this.obj.scale.setScalar(radius * startScale);
  }
  update(dt: number): boolean {
    this.t += dt;
    const k = Math.min(1, this.t / this.dur);
    const e = 1 - Math.pow(1 - k, 3);
    this.obj.scale.setScalar(this.radius * (this.startScale + (1 - this.startScale) * e));
    (this.mat.uniforms.uAlpha as { value: number }).value = 1 - k;
    return this.t < this.dur;
  }
  dispose(): void {
    this.obj.geometry.dispose();
    this.mat.dispose();
  }
}

// ---------------------------------------------------------------------------
// Ground flash disc (impact light pool).

const DISC_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uAlpha;
varying vec2 vUv;
void main() {
  float r = length(vUv * 2.0 - 1.0);
  float a = pow(max(0.0, 1.0 - r), 1.6);
  gl_FragColor = vec4(uColor * (1.0 + a * 2.0), a * uAlpha);
}`;

export class DiscEffect implements Effect {
  readonly obj: THREE.Mesh;
  private t = 0;
  private mat: THREE.ShaderMaterial;
  constructor(origin: THREE.Vector3, radius: number, color: THREE.Color, private dur = 0.3, private peak = 1) {
    this.mat = new THREE.ShaderMaterial({
      vertexShader: SIMPLE_VERT,
      fragmentShader: DISC_FRAG,
      uniforms: { uColor: { value: color.clone() }, uAlpha: { value: peak } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.obj = new THREE.Mesh(new THREE.PlaneGeometry(radius * 2, radius * 2), this.mat);
    this.obj.rotation.x = -Math.PI / 2;
    this.obj.position.copy(origin);
    this.obj.position.y += 0.05;
    this.obj.renderOrder = 11;
  }
  update(dt: number): boolean {
    this.t += dt;
    const k = Math.min(1, this.t / this.dur);
    (this.mat.uniforms.uAlpha as { value: number }).value = this.peak * (1 - k) * (1 - k);
    return this.t < this.dur;
  }
  dispose(): void {
    this.obj.geometry.dispose();
    this.mat.dispose();
  }
}

// ---------------------------------------------------------------------------
// Vertical light pillar / eruption column.

const PILLAR_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uAlpha;
uniform float uTime;
varying vec2 vUv;
float h(vec2 p){ return fract(sin(dot(p, vec2(12.9898,78.233))) * 43758.5453); }
float n(vec2 p){ vec2 i=floor(p); vec2 f=fract(p); vec2 u=f*f*(3.0-2.0*f);
  return mix(mix(h(i),h(i+vec2(1,0)),u.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),u.x),u.y); }
void main() {
  float y = vUv.y;
  float streak = n(vec2(vUv.x * 12.0, y * 3.0 - uTime * 4.0));
  float a = (1.0 - y) * (0.4 + streak * 0.8) * smoothstep(0.0, 0.08, y);
  gl_FragColor = vec4(uColor * (1.5 + streak * 1.5), a * uAlpha);
}`;

export class PillarEffect implements Effect {
  readonly obj: THREE.Mesh;
  private t = 0;
  private mat: THREE.ShaderMaterial;
  constructor(origin: THREE.Vector3, radius: number, height: number, color: THREE.Color, private dur = 0.6) {
    this.mat = new THREE.ShaderMaterial({
      vertexShader: SIMPLE_VERT,
      fragmentShader: PILLAR_FRAG,
      uniforms: { uColor: { value: color.clone() }, uAlpha: { value: 1 }, uTime: { value: 0 } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const g = new THREE.CylinderGeometry(radius, radius * 1.15, height, 24, 1, true);
    g.translate(0, height / 2, 0);
    this.obj = new THREE.Mesh(g, this.mat);
    this.obj.position.copy(origin);
    this.obj.renderOrder = 12;
  }
  update(dt: number): boolean {
    this.t += dt;
    const k = Math.min(1, this.t / this.dur);
    const u = this.mat.uniforms as { uAlpha: { value: number }; uTime: { value: number } };
    u.uTime.value = this.t;
    u.uAlpha.value = k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85;
    this.obj.scale.set(1 + k * 0.25, 0.4 + Math.min(1, k * 4) * 0.6, 1 + k * 0.25);
    return this.t < this.dur;
  }
  dispose(): void {
    this.obj.geometry.dispose();
    this.mat.dispose();
  }
}

// ---------------------------------------------------------------------------
// Beam / thrust streak along a rectangle.

const BEAM_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uAlpha;
uniform float uTime;
varying vec2 vUv;
void main() {
  float across = abs(vUv.x * 2.0 - 1.0);
  float core = pow(1.0 - across, 3.0);
  float flow = 0.7 + 0.3 * sin(vUv.y * 30.0 - uTime * 40.0);
  float tip = smoothstep(1.0, 0.85, vUv.y) * smoothstep(0.0, 0.05, vUv.y);
  gl_FragColor = vec4(mix(uColor, vec3(1.0), core * 0.5) * (1.5 + core * 3.0), core * flow * tip * uAlpha);
}`;

export class BeamEffect implements Effect {
  readonly obj: THREE.Mesh;
  private t = 0;
  private mat: THREE.ShaderMaterial;
  constructor(origin: THREE.Vector3, facing: number, length: number, width: number, color: THREE.Color, private dur = 0.3, offset = 0, height = 1.0) {
    this.mat = new THREE.ShaderMaterial({
      vertexShader: SIMPLE_VERT,
      fragmentShader: BEAM_FRAG,
      uniforms: { uColor: { value: color.clone() }, uAlpha: { value: 1 }, uTime: { value: 0 } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const g = new THREE.PlaneGeometry(width, length);
    g.rotateX(-Math.PI / 2);
    g.translate(0, 0, offset + length / 2);
    // Flip v so that 0 is at the caster.
    const uv = g.getAttribute('uv') as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i++) uv.setY(i, 1 - uv.getY(i));
    this.obj = new THREE.Mesh(g, this.mat);
    this.obj.position.copy(origin);
    this.obj.position.y += height;
    this.obj.rotation.y = facing;
    this.obj.renderOrder = 12;
  }
  update(dt: number): boolean {
    this.t += dt;
    const k = Math.min(1, this.t / this.dur);
    const u = this.mat.uniforms as { uAlpha: { value: number }; uTime: { value: number } };
    u.uTime.value = this.t;
    u.uAlpha.value = k < 0.1 ? k / 0.1 : 1 - (k - 0.1) / 0.9;
    return this.t < this.dur;
  }
  dispose(): void {
    this.obj.geometry.dispose();
    this.mat.dispose();
  }
}

// ---------------------------------------------------------------------------
// Cooling ground crack decal.

let crackTex: THREE.Texture | null = null;
function crackTexture(): THREE.Texture {
  if (crackTex) return crackTex;
  const s = 256;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const g = c.getContext('2d');
  if (!g) throw new Error('2D canvas unavailable');
  g.clearRect(0, 0, s, s);
  g.strokeStyle = 'rgba(255,255,255,1)';
  g.lineCap = 'round';
  const branch = (x: number, y: number, ang: number, len: number, w: number, depth: number) => {
    if (depth <= 0 || len < 4) return;
    let cx = x;
    let cy = y;
    g.lineWidth = w;
    g.beginPath();
    g.moveTo(cx, cy);
    const steps = 6;
    for (let i = 0; i < steps; i++) {
      ang += (Math.random() - 0.5) * 0.7;
      cx += (Math.cos(ang) * len) / steps;
      cy += (Math.sin(ang) * len) / steps;
      g.lineTo(cx, cy);
    }
    g.stroke();
    branch(cx, cy, ang + (Math.random() - 0.5) * 1.2, len * 0.6, w * 0.65, depth - 1);
    if (Math.random() < 0.6) branch(cx, cy, ang + (Math.random() - 0.5) * 1.8, len * 0.45, w * 0.5, depth - 1);
  };
  for (let i = 0; i < 9; i++) branch(s / 2, s / 2, (i / 9) * Math.PI * 2 + Math.random() * 0.4, s * 0.32, 5, 3);
  crackTex = new THREE.CanvasTexture(c);
  return crackTex;
}

const CRACK_FRAG = /* glsl */ `
uniform sampler2D map;
uniform vec3 uColor;
uniform float uHeat;
uniform float uAlpha;
varying vec2 vUv;
void main() {
  float m = texture2D(map, vUv).a;
  float r = length(vUv * 2.0 - 1.0);
  float fade = smoothstep(1.0, 0.6, r);
  vec3 hot = mix(uColor, vec3(1.0, 0.95, 0.8), uHeat * 0.5) * (0.4 + uHeat * 4.0);
  gl_FragColor = vec4(hot, m * fade * uAlpha);
}`;

export class CrackEffect implements Effect {
  readonly obj: THREE.Mesh;
  private t = 0;
  private mat: THREE.ShaderMaterial;
  constructor(origin: THREE.Vector3, radius: number, color: THREE.Color, private dur = 2.2) {
    this.mat = new THREE.ShaderMaterial({
      vertexShader: SIMPLE_VERT,
      fragmentShader: CRACK_FRAG,
      uniforms: { map: { value: crackTexture() }, uColor: { value: color.clone() }, uHeat: { value: 1 }, uAlpha: { value: 1 } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.obj = new THREE.Mesh(new THREE.PlaneGeometry(radius * 2, radius * 2), this.mat);
    this.obj.rotation.x = -Math.PI / 2;
    this.obj.rotation.z = Math.random() * Math.PI * 2;
    this.obj.position.copy(origin);
    this.obj.position.y += 0.04;
    this.obj.renderOrder = 9;
  }
  update(dt: number): boolean {
    this.t += dt;
    const k = Math.min(1, this.t / this.dur);
    const u = this.mat.uniforms as { uHeat: { value: number }; uAlpha: { value: number } };
    u.uHeat.value = Math.max(0, 1 - k * 2.2);
    u.uAlpha.value = 1 - k * k;
    return this.t < this.dur;
  }
  dispose(): void {
    this.obj.geometry.dispose();
    this.mat.dispose();
  }
}

// ---------------------------------------------------------------------------
// Ground telegraph: circle / cone / rect / ring with fill progress + hot edge.

const TELE_FRAG = /* glsl */ `
uniform float uProgress, uShape, uHalfAngle, uInner, uOpacity, uTime;
uniform vec3 uColor;
varying vec2 vUv;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float aa = fwidth(r) * 1.5;
  float inside, t, edge;
  if (uShape < 0.5) {
    inside = 1.0 - smoothstep(1.0 - aa, 1.0, r); t = r; edge = smoothstep(0.93, 1.0 - aa, r);
  } else if (uShape < 1.5) {
    float a = abs(atan(p.x, p.y)); float aA = fwidth(a) * 1.5;
    inside = (1.0 - smoothstep(1.0 - aa, 1.0, r)) * (1.0 - smoothstep(uHalfAngle - aA, uHalfAngle, a)); t = r;
    edge = max(smoothstep(0.93, 1.0 - aa, r), 1.0 - smoothstep(uHalfAngle - 0.06, uHalfAngle - aA, a));
  } else if (uShape < 2.5) {
    inside = 1.0; t = vUv.y;
    edge = max(max(1.0 - smoothstep(0.0, 0.05, vUv.x), smoothstep(0.95, 1.0, vUv.x)), smoothstep(0.97, 1.0, vUv.y));
  } else {
    inside = (1.0 - smoothstep(1.0 - aa, 1.0, r)) * smoothstep(uInner, uInner + aa, r);
    t = (r - uInner) / max(1e-3, 1.0 - uInner);
    edge = max(smoothstep(0.93, 1.0 - aa, r), 1.0 - smoothstep(uInner + aa, uInner + 0.06, r));
  }
  float filled = 1.0 - step(uProgress, t);
  float front = smoothstep(uProgress - 0.05, uProgress, t) * (1.0 - step(uProgress, t));
  float pulse = 0.85 + 0.15 * sin(uTime * 12.0);
  float a = inside * (0.14 + 0.30 * filled + 0.9 * front + 0.7 * edge) * uOpacity * pulse;
  gl_FragColor = vec4(mix(uColor, vec3(1.0, 0.9, 0.7), front * 0.5) * 1.4, a);
}`;

export const TELEGRAPH_COLORS: Record<string, THREE.Color> = {
  red: new THREE.Color(1.0, 0.16, 0.06),
  yellow: new THREE.Color(1.0, 0.78, 0.2),
  blue: new THREE.Color(0.25, 0.55, 1.0),
  purple: new THREE.Color(0.65, 0.25, 1.0),
  ally: new THREE.Color(0.55, 0.8, 1.0),
};

export type TeleShape =
  | { kind: 'circle'; radius: number; offset?: number }
  | { kind: 'cone'; radius: number; angle: number; rotation?: number }
  | { kind: 'rect'; length: number; width: number; offset?: number; rotation?: number }
  | { kind: 'ring'; inner: number; outer: number; offset?: number };

export class TelegraphEffect implements Effect {
  readonly obj: THREE.Mesh;
  private t = 0;
  private mat: THREE.ShaderMaterial;
  private done = false;
  constructor(origin: THREE.Vector3, facing: number, shape: TeleShape, color: THREE.Color, private delay: number, heightAt: (x: number, z: number) => number) {
    let geo: THREE.PlaneGeometry;
    let shapeId = 0;
    let half = Math.PI / 4;
    let inner = 0.5;
    const pos = origin.clone();
    let rot = facing;
    switch (shape.kind) {
      case 'circle': {
        geo = new THREE.PlaneGeometry(shape.radius * 2, shape.radius * 2, 16, 16);
        const o = shape.offset ?? 0;
        pos.x += Math.sin(facing) * o;
        pos.z += Math.cos(facing) * o;
        break;
      }
      case 'cone':
        geo = new THREE.PlaneGeometry(shape.radius * 2, shape.radius * 2, 16, 16);
        shapeId = 1;
        half = shape.angle / 2;
        rot = facing + (shape.rotation ?? 0);
        break;
      case 'rect': {
        geo = new THREE.PlaneGeometry(shape.width, shape.length, 4, 16);
        // +y becomes forward after the flat rotation below.
        geo.translate(0, shape.length / 2 + (shape.offset ?? 0), 0);
        shapeId = 2;
        rot = facing + (shape.rotation ?? 0);
        break;
      }
      case 'ring': {
        geo = new THREE.PlaneGeometry(shape.outer * 2, shape.outer * 2, 16, 16);
        shapeId = 3;
        inner = shape.inner / shape.outer;
        const o = shape.offset ?? 0;
        pos.x += Math.sin(facing) * o;
        pos.z += Math.cos(facing) * o;
        break;
      }
    }
    // Lay flat: plane +Y becomes -Z; rotate so local "up" (v=1 / +y) points along facing.
    geo.rotateX(-Math.PI / 2);
    geo.rotateY(Math.PI);
    // Drape over terrain.
    const p = geo.getAttribute('position') as THREE.BufferAttribute;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    for (let i = 0; i < p.count; i++) {
      const lx = p.getX(i);
      const lz = p.getZ(i);
      const wx = pos.x + lx * cos + lz * sin;
      const wz = pos.z - lx * sin + lz * cos;
      p.setY(i, heightAt(wx, wz) - pos.y + 0.07);
    }
    this.mat = new THREE.ShaderMaterial({
      vertexShader: SIMPLE_VERT,
      fragmentShader: TELE_FRAG,
      uniforms: {
        uProgress: { value: 0 },
        uShape: { value: shapeId },
        uHalfAngle: { value: half },
        uInner: { value: inner },
        uOpacity: { value: 0 },
        uTime: { value: 0 },
        uColor: { value: color.clone() },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    this.obj = new THREE.Mesh(geo, this.mat);
    this.obj.position.copy(pos);
    this.obj.rotation.y = rot;
    this.obj.renderOrder = 8;
  }
  /** Ends the telegraph early (area erupted or cancelled). */
  finish(): void {
    this.done = true;
  }
  update(dt: number): boolean {
    this.t += dt;
    const u = this.mat.uniforms as Record<string, { value: number }>;
    (u.uTime as { value: number }).value = this.t;
    (u.uProgress as { value: number }).value = Math.min(1, this.t / Math.max(0.05, this.delay));
    const fadeIn = Math.min(1, this.t / 0.12);
    const out = this.done ? 0 : 1;
    (u.uOpacity as { value: number }).value = fadeIn * out;
    return !this.done && this.t < this.delay + 0.3;
  }
  dispose(): void {
    this.obj.geometry.dispose();
    this.mat.dispose();
  }
}
